/**
 * Patches `JSON.stringify` to gracefully handle cyclic references instead of throwing.
 *
 * **Why this exists:**
 * PostHog's internal logger calls `JSON.stringify` on its own state during `_init`,
 * and that state contains cyclic references. Most browsers silently handle this, but
 * Mobile Safari / WKWebView (iOS) throws:
 *   "TypeError: JSON.stringify cannot serialize cyclic structures."
 *
 * This unhandled error crashes the app on iOS WebViews. Since the call happens inside
 * PostHog's bundled code (not in our wrapper), we cannot try/catch it directly. Instead,
 * we make `JSON.stringify` itself cycle-safe globally.
 *
 * **How it works:**
 * 1. The patched version first tries the original `JSON.stringify` (fast path).
 * 2. If it throws a cyclic-reference error, it retries with a `replacer` that
 *    substitutes repeated references with the string `"[Circular]"`.
 * 3. Any non-cyclic error is re-thrown as-is.
 *
 * Call `installSafeJsonStringify()` once at app startup, **before** PostHog or any
 * other library that might stringify cyclic objects.
 */

const nativeStringify = JSON.stringify;

function isCyclicError(error: unknown): boolean {
	if (!(error instanceof TypeError)) return false;
	const message = error.message.toLowerCase();
	return (
		message.includes("cyclic") ||
		message.includes("circular") ||
		message.includes("converting circular structure")
	);
}

function stringifyWithCycleGuard(
	value: unknown,
	replacer?: Parameters<typeof JSON.stringify>[1],
	space?: Parameters<typeof JSON.stringify>[2],
): string | undefined {
	const seen = new WeakSet();

	const cycleReplacer = (_key: string, val: unknown): unknown => {
		if (val !== null && typeof val === "object") {
			if (seen.has(val as object)) {
				return "[Circular]";
			}
			seen.add(val as object);
		}
		return val;
	};

	// When the caller already provided a function replacer, compose it with the cycle guard.
	if (typeof replacer === "function") {
		const userReplacer = replacer as (key: string, value: unknown) => unknown;
		return nativeStringify(
			value,
			function (this: unknown, key: string, val: unknown) {
				const transformed = userReplacer.call(this, key, val);
				return cycleReplacer(key, transformed);
			},
			space,
		);
	}

	// Array replacers act as allow-lists; cycles among allowed keys are still possible.
	if (Array.isArray(replacer)) {
		return nativeStringify(
			value,
			function (this: unknown, key: string, val: unknown) {
				// Root object (key === "") must always pass through.
				if (key === "" || (replacer as (string | number)[]).includes(key)) {
					return cycleReplacer(key, val);
				}
				return val;
			},
			space,
		);
	}

	return nativeStringify(value, cycleReplacer, space);
}

export function installSafeJsonStringify(): void {
	JSON.stringify = function safeStringify(
		value: unknown,
		replacer?: Parameters<typeof JSON.stringify>[1],
		space?: Parameters<typeof JSON.stringify>[2],
	): string | undefined {
		try {
			// biome-ignore lint/suspicious/noExplicitAny: JSON.stringify has complex overloads that don't unify cleanly
			return nativeStringify(value, replacer as any, space);
		} catch (error) {
			if (isCyclicError(error)) {
				// Retry with cycle detection — this is the fallback for iOS WKWebView.
				return stringifyWithCycleGuard(value, replacer, space);
			}
			throw error;
		}
	} as typeof JSON.stringify;
}
